import { Component } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime } from "rxjs/operators";

import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";

import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";

import { NavMenuService, OmniBarService, SecurityService, ThemeService } from "../../services";
import { ShellService } from "@tylertech/incode-module";


export const enum DrawerMode
{
    Modal = "modal",
    Dismissible = "dismissible",
}

@Component({
    selector: "app-outlet",
    templateUrl: "./app-outlet.tmpl.html",
    styleUrls: ["./app-outlet.component.scss"],
    standalone: false
})
export class AppOutletComponent
{
    private readonly log = getLoggerFor("AppOutletComponent");
    protected menuMode: DrawerMode;
    menuOpen = false;
    showOmni: boolean;
    embedded: boolean = false;
    isProvisional: boolean = false;
    constructor(
        breakpointObserver: BreakpointObserver,
        $omniBarService: OmniBarService,
        private readonly $navMenuService: NavMenuService,
        $securityService: SecurityService,
        private readonly $themeService: ThemeService,
        private readonly $shellService: ShellService,
    )
    {
        breakpointObserver.observe("(max-width: 1439px)")
            .pipe(debounceTime(500), takeUntilDestroyed())
            .subscribe((breakpointState: BreakpointState) =>
            {
                if (breakpointState.matches)
                {
                    this.menuMode = DrawerMode.Modal;
                    this.$navMenuService.closeMenu();
                }
                else
                {
                    this.menuMode = DrawerMode.Dismissible;
                    if (this.$navMenuService.IsNavPinned && !this.$navMenuService.IsMenuOpen)
                    {
                        this.$navMenuService.toggle(true);
                    }
                }
            });

        $navMenuService.toggleChange
            .pipe(takeUntilDestroyed())
            .subscribe((newValue: boolean) =>
            {
                this.toggleMenu(newValue);
            });

        $navMenuService.disabled
            .pipe(takeUntilDestroyed())
            .subscribe(disabled =>
            {
                if (disabled)
                {
                    this.closeMenu();
                }
            });

        $omniBarService.showOmnibar
            .pipe(takeUntilDestroyed())
            .subscribe((show: boolean) =>
            {
                this.showOmni = show;
            });

        $securityService.principleChange
            .pipe(takeUntilDestroyed())
            .subscribe((principle) =>
            {
                this.$navMenuService.setDisabled(!principle);

                if (!$securityService.isAuthenticated)
                {
                    this.menuOpen = false;
                }

                if (this.isModal)
                {
                    this.$navMenuService.closeMenu();
                }

                this.isProvisional = $securityService.isProvisional;
            });
    }

    ngOnInit(): void
    {
        this.embedded = this.$shellService.isEmbedded();
    }

    get componentClass(): Record<string, boolean>
    {
        const classes: Record<string, boolean> = {};

        const currentTheme = this.$themeService.currentTheme;
        classes[currentTheme.cssClass] = true;

        return classes;
    }

    get isModal(): boolean { return this.menuMode === DrawerMode.Modal; }

    get scaffoldClasses(): Record<string, boolean>
    {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            "nav-open": this.$navMenuService.IsNavPinned,
            "provisional": this.isProvisional,
            /* eslint-enable @typescript-eslint/naming-convention */
        };
    }

    get leftMenuTabIndex(): number
    {
        return this.$navMenuService.IsNavPinned || this.menuOpen ? 0 : -1;
    }

    @LogInvocation(LogLevel.DEBUG)
    protected toggleMenu(opened: boolean): void
    {
        this.menuOpen = opened;
    }

    @LogInvocation(LogLevel.DEBUG)
    closeMenu(): void
    {
        if (!this.isModal)
        {
            return;
        }

        this.$navMenuService.toggle();
    }
}
